import { Button, Group, Modal, Stack, Text, TextInput } from "@mantine/core";
import { useState, type ReactNode } from "react";

export interface ConfirmActionModalProps {
  readonly opened: boolean;
  readonly onClose: () => void;
  readonly onConfirm: () => void | Promise<void>;
  readonly title: string;
  /** Names the target record, for example "Remove Jane Doe from Alpha Church?" */
  readonly message: string;
  readonly confirmLabel?: string;
  /** When set, the user must type this exact text (usually the record name) before confirming. */
  readonly typeToConfirm?: string;
  readonly danger?: boolean;
  readonly loading?: boolean;
  /** Extra content between the message and the buttons, such as a field for a reason. */
  readonly children?: ReactNode;
  /** Holds the confirm button back until the extra content is complete. */
  readonly confirmDisabled?: boolean;
}

/** Every destructive action goes through this modal, which always identifies the target record. Never window.confirm. */
export function ConfirmActionModal({
  opened,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel = "Confirm",
  typeToConfirm,
  danger = false,
  loading = false,
  children,
  confirmDisabled = false,
}: ConfirmActionModalProps) {
  const [typed, setTyped] = useState("");
  const blocked = confirmDisabled || (typeToConfirm !== undefined && typed !== typeToConfirm);
  return (
    <Modal opened={opened} onClose={onClose} title={title} centered>
      <Stack gap="md">
        <Text size="sm">{message}</Text>
        {children}
        {typeToConfirm !== undefined && (
          <TextInput
            label={`Type "${typeToConfirm}" to confirm`}
            value={typed}
            onChange={(event) => {
              const next = event.currentTarget.value;
              setTyped(next);
            }}
            autoComplete="off"
          />
        )}
        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            {...(danger ? { color: "red" } : {})}
            onClick={() => {
              void onConfirm();
            }}
            disabled={blocked || loading}
            loading={loading}
          >
            {confirmLabel}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
